import dayjs from 'dayjs'
import { Check, Paperclip, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { AttachmentOutput, MessageDetail } from 'types/api'

import { useGetAttachment } from '@services/chat/queries/useGetAttachment'

import { cn } from '@/lib/utils'

function AttachmentItem({
  attachment,
  own
}: {
  attachment: AttachmentOutput
  own: boolean
}) {
  const { data: blob } = useGetAttachment({ id: attachment.id })
  if (!blob) return null

  const isImage = attachment.mimetype?.includes('image')
  const url = URL.createObjectURL(blob)

  if (isImage) {
    return (
      <img
        src={url}
        alt={attachment.name ?? ''}
        className="mt-2 max-w-[180px] rounded-xl"
      />
    )
  }

  const download = () => {
    const link = document.createElement('a')
    link.href = url
    link.download = attachment.name ?? 'attachment'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <button
      type="button"
      onClick={download}
      className={cn(
        'mt-2 flex items-center gap-1.5 text-sm font-medium underline-offset-2 hover:underline',
        own ? 'text-white' : 'text-primary'
      )}
    >
      <Paperclip className="h-4 w-4" />
      {attachment.name}
    </button>
  )
}

export function ChatBubble({
  message,
  own
}: {
  message: MessageDetail
  own: boolean
}) {
  const { t } = useTranslation('chat')

  return (
    <div className={cn('flex gap-2', own ? 'justify-end' : 'justify-start')}>
      {!own && (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center self-end rounded-full bg-primary text-white">
          <User className="h-4 w-4" />
        </span>
      )}

      <div
        className={cn(
          'flex max-w-[78%] flex-col',
          own ? 'items-end' : 'items-start'
        )}
      >
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2 text-sm',
            own
              ? 'rounded-br-md bg-primary text-white'
              : 'rounded-bl-md bg-white text-ink shadow-[0_2px_8px_rgba(4,33,37,0.06)]'
          )}
        >
          {!own && message.type === 'ADMIN' && (
            <p className="mb-0.5 text-xs font-bold text-primary">Admin</p>
          )}
          {message.message && (
            <p className="whitespace-pre-wrap break-words">{message.message}</p>
          )}
          {message.attachments?.map((a) => (
            <AttachmentItem key={a.id} attachment={a} own={own} />
          ))}
        </div>

        <div className="mt-0.5 flex items-center gap-1 px-1 text-[11px] text-muted-foreground">
          <span>{dayjs(message.created_at).format('DD.MM.YYYY HH:mm')}</span>
          {own && message.is_read && (
            <span className="flex items-center gap-0.5 text-primary">
              <Check className="h-3 w-3" />
              {t('form.common.seen')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
